import { useCallback, useMemo } from 'react';
import { IconEdit, IconEye, IconFilePlus } from '@tabler/icons-react';
import { AppTable } from 'components/organisms';
import { commonTexts, journalTexts } from 'consts/text';
import { Option } from 'components/atoms';
import { QualificationDTO } from 'types/qualifications';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { generatePath } from 'react-router-dom';
import { appPaths } from 'consts/paths/paths';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { useJournalAbilities } from 'abilities';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { useSpecialJournalQualificationsPageNetworkManage } from './hooks/useSpecialJournalQualificationsPageNetworkManage';
import {
    columnsWithoutSensitiveData,
    columnsWithSensitiveData,
} from './SpecialJournalQualificationPage.utils';

export const SpecialJournalQualificationPage = () => {
    const { journalId } = useJournalId();
    const { statefulNavigate } = useNavigateWithState();
    const {
        specialJournalsAbilities: {
            checkIfCanManageQualificationBasis,
            checkIfCanSeeChildrenSensitiveData,
        },
    } = useJournalAbilities();

    const { journalData } = useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'SPECIAL',
    });

    const canManageQualificationBasis = checkIfCanManageQualificationBasis();
    const canSeeChildrenSensitiveData = checkIfCanSeeChildrenSensitiveData();

    const groupId = useMemo(() => journalData?.group.id, [journalData]);

    const isChildInTheGroup = useCallback(
        (qualification: QualificationDTO) => {
            if (!groupId) {
                return true;
            }
            return qualification.groups.some((group) => group.id === groupId && !group.leftGroup);
        },
        [groupId],
    );

    const isQualificationFilled = (qualification: QualificationDTO) => qualification.filled;

    const navigateToQualificationDetails = (qualification: QualificationDTO) =>
        statefulNavigate(
            generatePath(
                appPaths.app.journals.specialJournalDetails.pickedJournal.qualifications.details
                    .pickedQualification,
                {
                    qualificationId: qualification.id,
                    journalId,
                },
            ),
        );

    const navigateToQualificationAdd = (qualification: QualificationDTO) =>
        statefulNavigate(
            generatePath(
                appPaths.app.journals.specialJournalDetails.pickedJournal.qualifications.add,
                {
                    journalId,
                },
            ),
            {
                state: {
                    childId: qualification.childId,
                },
            },
        );

    const navigateToQualificationEdit = (qualification: QualificationDTO) =>
        statefulNavigate(
            generatePath(
                appPaths.app.journals.specialJournalDetails.pickedJournal.qualifications.edit
                    .pickedQualification,
                {
                    journalId,
                    qualificationId: qualification.id,
                },
            ),
            {
                state: {
                    childId: qualification.childId,
                },
            },
        );

    const options: Option<QualificationDTO>[] = [
        {
            label: commonTexts.actionLabels.details,
            onClick: navigateToQualificationDetails,
            Icon: <IconEye />,
            checkIfVisible: (qualification) => isQualificationFilled(qualification),
        },
        {
            label: journalTexts.journalDetails.specialJournal.qualifications.addQualification,
            onClick: navigateToQualificationAdd,
            Icon: <IconFilePlus />,
            checkIfVisible: (qualification) =>
                canManageQualificationBasis && !isQualificationFilled(qualification),
        },
        {
            label: journalTexts.journalDetails.specialJournal.qualifications.editQualification,
            onClick: navigateToQualificationEdit,
            Icon: <IconEdit />,
            checkIfVisible: (qualification) =>
                canManageQualificationBasis && isQualificationFilled(qualification),
        },
    ];

    const {
        qualificationsData,
        isQualificationsDataInitialLoading,
        isQualificationsDataError,
        isQualificationsDataRefetching,
    } = useSpecialJournalQualificationsPageNetworkManage({ journalId });

    // Sort qualifications: children who have left the group (leftGroup=true for group with journalId) go to the end
    const sortedQualificationsData = useMemo(
        () =>
            qualificationsData
                ? [...qualificationsData].sort((a, b) => {
                      const aGroup = a.groups.find((group) => group.id === groupId);
                      const bGroup = b.groups.find((group) => group.id === groupId);
                      const aLeft = aGroup?.leftGroup ?? false;
                      const bLeft = bGroup?.leftGroup ?? false;
                      if (aLeft === bLeft) return 0;
                      return aLeft ? 1 : -1;
                  })
                : qualificationsData,
        [qualificationsData, groupId],
    );

    const columns = canSeeChildrenSensitiveData
        ? columnsWithSensitiveData
        : columnsWithoutSensitiveData;

    return (
        <AppTable
            title={commonTexts.dataLabels.childList}
            emptyInfo={
                journalTexts.journalDetails.specialJournal.qualifications.emptyQualificationList
            }
            options={options}
            columns={columns}
            dataSource={sortedQualificationsData}
            rowKey={({ childId }) => childId}
            isOptionsLoading={isQualificationsDataInitialLoading}
            isError={isQualificationsDataError}
            isDataInitialLoading={isQualificationsDataInitialLoading}
            isDataRefetching={isQualificationsDataRefetching}
            isTableManageUtilsHidden
            onRow={(qualification) => ({
                style: {
                    opacity: !isChildInTheGroup(qualification) ? 0.6 : 1,
                },
            })}
        />
    );
};
